import { useState } from 'react'
import { Icon } from '@iconify/react'
import { deleteAPI, put } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import TagForm from './components/TagForm'
import type { Tag } from '../../../../components/tags/TagChips'
import '../../../../components/tags/tags.scss'

/**
 * Tag management. The sidebar entry is shown only to super admin and customer
 * service; a manager or agent who navigates here directly gets the list
 * read-only, since reading tags is open to every role but the create/edit/
 * delete routes are not. `canManage` mirrors the API gate rather than trusting
 * the hidden nav.
 */
function Tags() {
  const { notify, profile, tags, tagsLoading, fetchTags } = useGlobalContext()
  const [showForm, setShowForm] = useState(false)
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [showDeleted, setShowDeleted] = useState(false)
  const [search, setSearch] = useState('')

  const canManage =
    profile?.role === 'super admin' || profile?.role === 'customer service'

  const all = (tags as Tag[] | undefined) ?? []
  const activeCount = all.filter((tag) => tag.isActive).length
  const deletedCount = all.length - activeCount

  const visible = all
    .filter((tag) => (showDeleted ? !tag.isActive : tag.isActive))
    .filter((tag) =>
      search ? tag.name.toLowerCase().includes(search.toLowerCase()) : true,
    )

  const removeTag = (tag: Tag) => {
    // Deleting is reversible — the tag is deactivated, not stripped from the
    // shipments carrying it — so a plain confirm is proportionate here.
    if (
      !window.confirm(
        `Delete the tag "${tag.name}"? It will stop appearing in the tag picker, but shipments already tagged with it keep the tag and stay filterable.`,
      )
    ) {
      return
    }
    setBusyId(tag._id)
    deleteAPI(`admin/tags/${tag._id}`)
      .then((result) => {
        if (result.status === 200) {
          notify('success', result.message)
          fetchTags()
        } else {
          notify('error', result.message)
        }
        setBusyId(null)
      })
      .catch(() => {
        notify('error', 'Could not delete tag')
        setBusyId(null)
      })
  }

  const restoreTag = (tag: Tag) => {
    setBusyId(tag._id)
    put(`admin/tags/${tag._id}`, { isActive: true })
      .then((result) => {
        if (result.status === 200) {
          notify('success', `"${tag.name}" restored`)
          fetchTags()
        } else {
          notify('error', result.message)
        }
        setBusyId(null)
      })
      .catch(() => {
        notify('error', 'Could not restore tag')
        setBusyId(null)
      })
  }

  const openCreate = () => {
    setSelectedTag(null)
    setShowForm(true)
  }

  return (
    <div className='tags-page'>
      {showForm && (
        <TagForm
          setShowForm={setShowForm}
          onSaved={fetchTags}
          selectedTag={selectedTag}
        />
      )}

      <div className='tags-hero'>
        <div className='hero-text'>
          <h1>Shipment Tags</h1>
          <p>
            Label shipments with your own categories, then filter the booking
            lists by them. Deleting a tag hides it from the picker but never
            removes it from shipments already tagged.
          </p>
        </div>
        <div className='hero-stats'>
          <div className='stat'>
            <h2>{activeCount}</h2>
            <p>Active</p>
          </div>
          <div className='stat'>
            <h2>{deletedCount}</h2>
            <p>Deleted</p>
          </div>
        </div>
      </div>

      <div className='tags-toolbar'>
        <div className='left-controls'>
          <div className='search'>
            <Icon icon='mdi:magnify' />
            <input
              type='text'
              placeholder='Search tags'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className='view-switch'>
            <button
              className={!showDeleted ? 'active' : ''}
              onClick={() => setShowDeleted(false)}
            >
              Active
            </button>
            <button
              className={showDeleted ? 'active' : ''}
              onClick={() => setShowDeleted(true)}
            >
              Deleted
            </button>
          </div>
        </div>
        {canManage && (
          <button className='create-btn' onClick={openCreate}>
            <Icon icon='mdi:plus' />
            Create Tag
          </button>
        )}
      </div>

      {tagsLoading ? (
        <FormLoader position='center' />
      ) : visible.length === 0 ? (
        <div className='tags-empty'>
          <Icon icon='mdi:tag-multiple-outline' />
          {search ? (
            <>
              <h3>No tags match “{search}”</h3>
              <p>Try a different search term.</p>
            </>
          ) : showDeleted ? (
            <>
              <h3>No deleted tags</h3>
              <p>Tags you delete will appear here, ready to be restored.</p>
            </>
          ) : (
            <>
              <h3>No tags yet</h3>
              <p>
                {canManage
                  ? 'Create your first tag to start labelling shipments — things like “Fragile”, “VIP customer” or “Awaiting customs doc”.'
                  : 'Ask a super admin or customer service to create one.'}
              </p>
              {canManage && (
                <button onClick={openCreate}>
                  <Icon icon='mdi:plus' />
                  Create Tag
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className='tags-grid'>
          {visible.map((tag) => (
            <div
              key={tag._id}
              className={`tag-card ${tag.isActive ? '' : 'deleted'}`}
            >
              <div className='card-top'>
                <span
                  className='tag-chip'
                  style={{
                    background: `${tag.colour}1A`,
                    color: tag.colour,
                    border: `1px solid ${tag.colour}55`,
                  }}
                >
                  {tag.name}
                </span>
                {canManage && (
                  <div className='actions'>
                    {busyId === tag._id ? (
                      <FormLoader position='left' />
                    ) : tag.isActive ? (
                      <>
                        <button
                          title='Edit tag'
                          onClick={() => {
                            setSelectedTag(tag)
                            setShowForm(true)
                          }}
                        >
                          <Icon icon='mdi:pencil-outline' />
                        </button>
                        <button
                          className='danger'
                          title='Delete tag'
                          onClick={() => removeTag(tag)}
                        >
                          <Icon icon='mdi:trash-can-outline' />
                        </button>
                      </>
                    ) : (
                      <button
                        title='Restore tag'
                        onClick={() => restoreTag(tag)}
                      >
                        <Icon icon='mdi:restore' />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className='meta'>
                <span>
                  <Icon icon='mdi:account-outline' />
                  {tag.createdBy?.adminName || 'Unknown'}
                </span>
                <span>
                  <Icon icon='mdi:calendar-blank-outline' />
                  {tag.createdAt
                    ? new Date(tag.createdAt).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—'}
                </span>
              </div>

              {!tag.isActive && (
                <div className='deleted-flag'>
                  Deleted
                  {tag.deactivatedBy?.adminName
                    ? ` by ${tag.deactivatedBy.adminName}`
                    : ''}
                  . Still shown on shipments already tagged.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Tags
